/**
 * Diary Firestore Service (web) — port of services/firebase/diary.ts.
 */
import {
  collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, deleteDoc, onSnapshot, writeBatch,
  query, where, orderBy, limit as qLimit, serverTimestamp,
  type DocumentSnapshot, type QueryDocumentSnapshot, type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from './config';
import type { DiaryEntry, DiaryEntryInput, MealTime } from '@/types/diary';

type Snap = DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>;

function toDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return undefined;
}

function toEntry(snap: Snap): DiaryEntry {
  const data = (snap.data() || {}) as Record<string, unknown>;
  return { ...data, id: snap.id, timestamp: toDate(data.timestamp), createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as unknown as DiaryEntry;
}

const entries = () => collection(db, COLLECTIONS.DIARY_ENTRIES);

export class DiaryService {
  static async createEntry(entry: DiaryEntryInput): Promise<string> {
    // Deep-clean undefined values (mobile parity); the timestamp is re-applied as a Date.
    const clean = JSON.parse(JSON.stringify(entry));
    const ref = await addDoc(entries(), { ...clean, timestamp: entry.timestamp || serverTimestamp(), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return ref.id;
  }

  static async getEntry(entryId: string): Promise<DiaryEntry | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.DIARY_ENTRIES, entryId));
    return snap.exists() ? toEntry(snap) : null;
  }

  static async getEntriesForDate(userId: string, date: string): Promise<DiaryEntry[]> {
    const q = query(entries(), where('userId', '==', userId), where('date', '==', date), orderBy('timestamp', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(toEntry);
  }

  static subscribeToEntriesForDate(userId: string, date: string, onUpdate: (entries: DiaryEntry[]) => void, onError?: (error: Error) => void): () => void {
    const q = query(entries(), where('userId', '==', userId), where('date', '==', date), orderBy('timestamp', 'asc'));
    return onSnapshot(q, (snap) => onUpdate(snap.docs.map(toEntry)), (err) => onError?.(err));
  }

  static async updateEntry(entryId: string, updates: Partial<DiaryEntry>): Promise<void> {
    const { id: _id, createdAt: _c, timestamp, ...updateData } = updates;
    const clean = JSON.parse(JSON.stringify(updateData));
    await updateDoc(doc(db, COLLECTIONS.DIARY_ENTRIES, entryId), { ...clean, ...(timestamp !== undefined && { timestamp }), updatedAt: serverTimestamp() });
  }

  static async deleteEntry(entryId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.DIARY_ENTRIES, entryId));
  }

  static async deleteEntriesBySourceId(userId: string, sourceId: string): Promise<void> {
    try {
      const snap = await getDocs(query(entries(), where('userId', '==', userId), where('sourceId', '==', sourceId)));
      if (snap.empty) return;
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    } catch (e) {
      console.warn('Failed to cascade-delete diary entries for sourceId:', sourceId, e);
    }
  }

  static async getRecentEntries(userId: string, limit = 10): Promise<DiaryEntry[]> {
    const snap = await getDocs(query(entries(), where('userId', '==', userId), orderBy('createdAt', 'desc'), qLimit(limit * 3)));
    const seen = new Set<string>();
    const out: DiaryEntry[] = [];
    for (const d of snap.docs) {
      if (out.length >= limit) break;
      const name = (d.data().name as string | undefined)?.toLowerCase();
      if (name && !seen.has(name)) {
        seen.add(name);
        out.push(toEntry(d));
      }
    }
    return out;
  }

  static async getEntriesForDateRange(userId: string, startDate: string, endDate: string): Promise<DiaryEntry[]> {
    const q = query(entries(), where('userId', '==', userId), where('date', '>=', startDate), where('date', '<=', endDate), orderBy('date', 'asc'), orderBy('timestamp', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(toEntry);
  }

  static async entryExistsForSource(userId: string, date: string, source: string, sourceId: string): Promise<boolean> {
    try {
      const snap = await getDocs(query(entries(), where('userId', '==', userId), where('date', '==', date), where('source', '==', source), where('sourceId', '==', sourceId), qLimit(1)));
      return !snap.empty;
    } catch {
      return false;
    }
  }

  private static copyPayload(entry: DiaryEntry, targetDate: string, targetMealType?: MealTime) {
    const { id: _i, createdAt: _c, updatedAt: _u, timestamp: _t, ...entryData } = entry;
    return { ...JSON.parse(JSON.stringify(entryData)), date: targetDate, mealType: targetMealType || entry.mealType, timestamp: new Date(), createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  }

  static async copyEntriesForDate(userId: string, sourceDate: string, targetDate: string, targetMealType?: MealTime): Promise<number> {
    const source = await this.getEntriesForDate(userId, sourceDate);
    if (source.length === 0) return 0;
    const batch = writeBatch(db);
    for (const entry of source) batch.set(doc(entries()), this.copyPayload(entry, targetDate, targetMealType));
    await batch.commit();
    return source.length;
  }

  static async copySingleEntry(entry: DiaryEntry, targetDate: string, targetMealType?: MealTime): Promise<string> {
    const ref = doc(entries());
    await setDoc(ref, this.copyPayload(entry, targetDate, targetMealType));
    return ref.id;
  }

  static async copyMultipleEntries(list: DiaryEntry[], targetDate: string, targetMealType?: MealTime): Promise<number> {
    const toCopy = list.filter((e) => e.date !== targetDate);
    if (toCopy.length === 0) return 0;
    const batch = writeBatch(db);
    for (const entry of toCopy) batch.set(doc(entries()), this.copyPayload(entry, targetDate, targetMealType));
    await batch.commit();
    return toCopy.length;
  }

  static async deleteEntriesForDate(userId: string, date: string): Promise<void> {
    const snap = await getDocs(query(entries(), where('userId', '==', userId), where('date', '==', date)));
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}
